import { View, Text } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DollarSign, RefreshCw, Clock } from 'lucide-react-native';
import { clientLogger } from '@/lib/datadog';
import { colors, typography } from '@/lib/theme';
import { trpc, trpcClient } from '@/lib/trpc';

export function OddsUpdaterStatus() {
  const queryClient = useQueryClient();
  const contentStyle = { gap: 16 };
  const buttonGroupStyle = { paddingTop: 8 };
  const refreshIconStyle = { marginRight: 8 };
  const refreshIconUpdatingStyle = { transform: [{ rotate: '360deg' }] };
  const { data: status, refetch } = useQuery(
    trpc.admin.oddsUpdaterStatus.queryOptions(undefined, {
      refetchInterval: 30000,
    }),
  );

  const triggerUpdate = async () => {
    try {
      await trpcClient.admin.triggerOddsUpdater.mutate();
      await queryClient.invalidateQueries(trpc.nfl.weeks.queryFilter());
      setTimeout(() => refetch(), 1000);
    } catch (error) {
      clientLogger.error('Failed to trigger odds update', { error });
    }
  };

  const formatLastUpdate = (lastUpdateTime: string | null) => {
    if (!lastUpdateTime) return 'Never';

    const date = new Date(lastUpdateTime);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (!status) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            <View style={styles.titleRow}>
              <DollarSign size={20} color={colors.text.primary} style={styles.iconGap} />
              <Text style={styles.titleText}>
                Odds Updater Status
              </Text>
            </View>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Text style={styles.bodyText}>Loading odds updater status...</Text>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <View style={styles.titleRow}>
            <DollarSign size={20} color={colors.text.primary} style={styles.iconGap} />
            <Text style={styles.titleText}>
              Odds Updater Status
            </Text>
          </View>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <View style={contentStyle}>
          <View style={styles.statusRow}>
            <Text style={styles.labelText}>Service Status:</Text>
            <Badge variant={status.isRunning ? "default" : "destructive"}>
              <Text style={styles.badgeText}>
                {status.isRunning ? "Running" : "Stopped"}
              </Text>
            </Badge>
          </View>

          <View style={styles.statusRow}>
            <Text style={styles.labelText}>Currently Updating:</Text>
            <Badge variant={status.isUpdating ? "secondary" : "outline"}>
              <Text style={status.isUpdating ? styles.badgeText : styles.mutedBadgeText}>
                {status.isUpdating ? "Yes" : "No"}
              </Text>
            </Badge>
          </View>

          {Boolean(status.lastUpdateTime) && (
            <View style={styles.statusRow}>
              <Text style={styles.labelText}>Last Update:</Text>
              <View style={styles.titleRow}>
                <Clock size={16} color={colors.text.secondary} style={styles.smallIconGap} />
                <Text style={styles.bodyText}>
                  {formatLastUpdate(status.lastUpdateTime)}
                </Text>
              </View>
            </View>
          )}

          <View style={buttonGroupStyle}>
            <Button
              onPress={triggerUpdate}
              disabled={status.isUpdating}
              variant="outline"
              style={styles.fullButton}
            >
              <RefreshCw
                size={16}
                color={colors.text.primary}
                style={[refreshIconStyle, status.isUpdating && refreshIconUpdatingStyle]}
              />
              <Text style={styles.buttonText}>
                {status.isUpdating ? "Updating..." : "Trigger Odds Update"}
              </Text>
            </Button>
          </View>

          <View style={styles.schedulePanel}>
            <Text style={styles.scheduleTitle}>
              Update Schedule:
            </Text>
            <Text style={styles.scheduleText}>
              • Once per day on non-game days{'\n'}
              • Every hour on game days{'\n'}
              • Only updates games that have not started
            </Text>
          </View>
        </View>
      </CardContent>
    </Card>
  );
}

const styles = StyleSheet.create(() => ({
  badgeText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  bodyText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.body,
    fontSize: 14,
  },
  buttonText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  fullButton: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
  },
  iconGap: {
    marginRight: 8,
  },
  labelText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 14,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  mutedBadgeText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  schedulePanel: {
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    padding: 12,
  },
  scheduleText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.body,
    fontSize: 12,
    lineHeight: 16,
  },
  scheduleTitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  smallIconGap: {
    marginRight: 4,
  },
  statusRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  titleRow: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  titleText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 18,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
}));
